<script setup lang="ts">
import { EIsoCode, ELanguage } from './UiLanguage.types'
import { LANGUAGES } from '~/components/UiLanguage/consts'

const { locales, locale } = useI18n()
const switchLocalePath = useSwitchLocalePath()
const isOpenLang = ref(false)
const isFocusLang = ref(false)

const availableLocales = computed(() => locales.value.filter(i => i.code !== locale.value))
const activeLanguage = computed(() => getLanguageName(locale.value))

function setOpenLang(val: boolean): void {
  isOpenLang.value = val
}

function setLangFocus(val: boolean): void {
  isFocusLang.value = val
}

function getLanguageName(isoCode: string): ELanguage {
  return isoCode.toUpperCase() === EIsoCode.PL
    ? ELanguage.Polish
    : ELanguage.English
}
</script>

<template>
  <div
    class="languages"
    :class="{ 'is-open': isOpenLang }"
    tabindex="0"
    @blur="setLangFocus(false)"
    @focus="setLangFocus(true)"
    @keypress.enter="setOpenLang(!isOpenLang)"
    @click="setOpenLang(!isOpenLang)"
  >
    <!-- eslint-disable-next-line vue/no-v-html -->
    <button
      class="languages--active"
      @blur="setLangFocus(false)"
      @focus="setLangFocus(true)"
    >
      <img
        :src="LANGUAGES[activeLanguage]"
        :alt="activeLanguage"
        width="16"
        height="16"
      >
    </button>
    <ul v-if="isOpenLang">
      <li
        v-for="(loc, index) in availableLocales"
        :key="index"
      >
        <a
          tabindex="0"
          :href="switchLocalePath(loc.code)"
          class="languages__item"
          @blur="setLangFocus(false)"
          @focus="setLangFocus(true)"
        >
          <img
            :src="LANGUAGES[getLanguageName(loc.code)]"
            :alt="getLanguageName(loc.code)"
            width="16"
            height="16"
          >
        </a>
      </li>
    </ul>
  </div>
</template>

<style lang="scss" scoped>

.languages {
  position: relative;
  width: 7.2rem;
  height: 4.6rem;
  padding: 1.2rem 1.6rem;
  background-color: transparent;
  border: 1px solid $gray-500;
  border-radius: 5rem;

  &--active {
    position: relative;
    width: 100%;
    font-weight: 500;
    font-size: 1.5rem;
    line-height: 2rem;
    letter-spacing: 0.01em;

    &:before {
      position: absolute;
      top: 50%;
      right: 0;
      width: 0.7rem;
      height: 0.7rem;
      background-image: url('/icons/chevron_down.svg');
      background-position: center;
      background-size: cover;
      transform: translateY(-50%);
      transition: transform .25s ease-in-out;
      content: '';
    }
  }

  &.is-open {
    .languages--active {
      &:before {
        transform: translateY(-50%) rotate(180deg);
      }
    }
  }

  &__item {
    position: absolute;
    top: -4.6rem;
    left: 0;
    display: flex;
    align-items: center;
    width: 100%;
    height: 4.6rem;
    padding: 1.2rem 1.6rem;
    font-weight: 500;
    font-size: 1.5rem;
    line-height: 2rem;
    letter-spacing: 0.01em;
    background-color: transparent;
    border: 1px solid $gray-500;
    border-radius: 5rem;

    @include atM {
      top: 4.6rem;
    }
  }
}
</style>
